import { isMyMessage, isVoiceMessage, SendbirdGroupChannel, SendbirdMessage } from '@sendbird/uikit-utils';
import type { TChannelPreview, TPreviewLabels } from '../types';
import { parseChatDocument } from './chatDocument';
import { isWelcomeMessage } from './welcomeMessage';

export const getMessageLabel = (message: SendbirdMessage, labels: TPreviewLabels): string => {
    const named = (kind: string, name?: string) => (name ? `${kind} · ${name}` : kind);

    const document = message.isUserMessage() ? parseChatDocument(message.data) : null;
    if (document) {
        if (document.fileType.startsWith('image/')) return labels.image;
        return named(labels.attachment, document.fileName || message.message?.trim());
    }
    if (message.isFileMessage()) {
        const type = message.type ?? '';
        if (isVoiceMessage(message)) return labels.voice;
        if (type.startsWith('image/')) return labels.image;
        if (type.startsWith('video/')) return named(labels.video, message.name);
        if (type.startsWith('audio/')) return named(labels.audio, message.name);
        return named(labels.attachment, message.name);
    }
    const text = message.message?.trim();
    return text ? text : labels.message;
};

export const isImageAttachment = (message: SendbirdMessage): boolean => {
    if (message.isFileMessage()) return (message.type ?? '').startsWith('image/');
    const document = message.isUserMessage() ? parseChatDocument(message.data) : null;
    return Boolean(document?.fileType.startsWith('image/'));
};

export const getChannelPreview = (
    message: SendbirdMessage | null | undefined,
    currentUserId: string | undefined,
    labels: TPreviewLabels,
): TChannelPreview | null => {
    if (!message || message.isAdminMessage()) return null;
    return {
        prefix: isMyMessage(message, currentUserId) ? labels.you : null,
        body: getMessageLabel(message, labels),
    };
};

const getActivityTime = (channel: SendbirdGroupChannel): number => {
    const message = channel.lastMessage;
    return message && !isWelcomeMessage(message) ? message.createdAt : -1;
};

export const getChannelsRenderKey = (channels: SendbirdGroupChannel[]): string =>
    channels
        .map(channel => {
            const message = channel.lastMessage;
            return [
                channel.url,
                channel.name,
                channel.customType,
                channel.unreadMessageCount,
                message?.messageId,
                message?.updatedAt,
                message && 'sendingStatus' in message ? message.sendingStatus : '',
            ].join('|');
        })
        .join(',');

export const sortChannelsByLastMessage = (channels: SendbirdGroupChannel[]): SendbirdGroupChannel[] =>
    [...channels].sort((a, b) => getActivityTime(b) - getActivityTime(a));
